// src/pages/SwitchInLandingPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  BarChart3,
  Layers,
  Zap,
  Smartphone,
  ArrowRight,
  CheckCircle2,
  Gem,
  ReceiptIndianRupee,
  Bell,
  Users,
  ShoppingBag,
  FileSpreadsheet,
  History,
  Clock3,
  ClipboardList,
  Headset,
  Database,
  Cloud,
  Lock,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { useSubmitEnquiryMutation } from "../toolkitServices/enquiryServiceApi";
import toast from "react-hot-toast";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const getSlide3DVariants = (compact) => ({
  enter: (direction) =>
    compact
      ? {
          x: direction > 0 ? 48 : -48,
          opacity: 0,
          scale: 0.97,
        }
      : {
          x: direction > 0 ? 220 : -220,
          opacity: 0,
          rotateY: direction > 0 ? -28 : 28,
          scale: 0.86,
          filter: "blur(6px)",
        },
  center: {
    x: 0,
    opacity: 1,
    rotateY: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: compact ? 0.45 : 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: (direction) =>
    compact
      ? {
          x: direction < 0 ? 48 : -48,
          opacity: 0,
          scale: 0.97,
          transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        }
      : {
          x: direction < 0 ? 220 : -220,
          opacity: 0,
          rotateY: direction < 0 ? -28 : 28,
          scale: 0.86,
          filter: "blur(6px)",
          transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
        },
});

const getDetailPanelVariants = (compact) => ({
  enter: (direction) => ({
    opacity: 0,
    y: compact ? 16 : direction > 0 ? 24 : -24,
    x: compact ? 0 : direction > 0 ? 12 : -12,
  }),
  center: {
    opacity: 1,
    y: 0,
    x: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.06 },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.2 },
  },
});

const floatOrb = (duration = 10, delay = 0) => ({
  y: [0, -24, 0, 16, 0],
  x: [0, 14, 0, -10, 0],
  scale: [1, 1.06, 1, 0.96, 1],
  transition: {
    duration,
    delay,
    repeat: Infinity,
    ease: "easeInOut",
  },
});

const screenPreviews = [
  {
    title: "Retail billing screen",
    desc: "Item search, customer, old gold/exchange and split payments in one jewellery-focused view.",
    img: "/invoice.png",
  },
  {
    title: "Stock & sales insights",
    desc: "Category-wise, design-wise, purity-wise stock with opening, inward, outward and closing summary.",
    img: "/stockinsights.png", // abhi same image use kar lo
  },
  {
    title: "Gold scheme dashboard",
    desc: "Active members, instalment schedule and maturity status in a clean owner overview.",
    img: "/invoice.png",
  },
  {
    title: "Customer profile",
    desc: "Full purchase history, schemes, birthdays/anniversaries and follow-up notes per customer.",
    img: "/customer.png",
  },
];

const previewHighlights = [
  [
    { label: "Purity + making + wastage fields", tone: "violet" },
    { label: "Old gold / exchange built-in", tone: "amber" },
    { label: "Split payments & quick totals", tone: "emerald" },
    { label: "Print / WhatsApp invoice", tone: "sky" },
  ],
  [
    { label: "Purity-wise stock snapshot", tone: "violet" },
    { label: "Branch-wise filters", tone: "sky" },
    { label: "Low stock alerts", tone: "amber" },
    { label: "Fast moving designs", tone: "emerald" },
  ],
  [
    { label: "Installment schedule view", tone: "violet" },
    { label: "Due reminder tracking", tone: "amber" },
    { label: "Maturity monitoring", tone: "emerald" },
    { label: "Collection summary daily", tone: "sky" },
  ],
  [
    { label: "Full purchase history", tone: "violet" },
    { label: "Follow-up reminders", tone: "emerald" },
    { label: "Occasions & preferences", tone: "amber" },
    { label: "Notes for staff", tone: "sky" },
  ],
];

const toneDotClass = {
  violet: "bg-violet-500",
  amber: "bg-amber-400",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
};

const SwitchInLandingPage = () => {
  const { canInstall, install, isInstalled } = usePwaInstall();
  const [submitEnquiry, { isLoading: enquiryLoading, isSuccess }] =
    useSubmitEnquiryMutation();
  const [enquiry, setEnquiry] = useState({
    storeName: "",
    mobileNumber: "",
    city: "",
    state: "",
    numberOfBranches: "1",
    description: "",
  });
  const [enquirySuccess, setEnquirySuccess] = useState("");
  const [activePreview, setActivePreview] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [progressKey, setProgressKey] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isCompactView, setIsCompactView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const slide3DVariants = useMemo(
    () => getSlide3DVariants(isCompactView || prefersReducedMotion),
    [isCompactView, prefersReducedMotion],
  );
  const detailPanelVariants = useMemo(
    () => getDetailPanelVariants(isCompactView),
    [isCompactView],
  );

  const heroMouseX = useMotionValue(0);
  const heroMouseY = useMotionValue(0);
  const heroRotateX = useSpring(useTransform(heroMouseY, [-0.5, 0.5], [10, -10]), {
    stiffness: 150,
    damping: 20,
  });
  const heroRotateY = useSpring(useTransform(heroMouseX, [-0.5, 0.5], [-10, 10]), {
    stiffness: 150,
    damping: 20,
  });

  // Pricing toggle
  const [billing, setBilling] = useState("yearly"); // "monthly" | "yearly"

  const pricing = useMemo(() => {
    const monthly = 2000;
    const yearly = 22000; // yearly package
    const effectiveMonthly = Math.round(yearly / 12);

    return {
      monthly,
      yearly,
      effectiveMonthly,
      savings: monthly * 12 - yearly,
    };
  }, []);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Enquiry submitted successfully!");
    }
  }, [isSuccess]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const widthMq = window.matchMedia("(max-width: 1023px)");
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onWidth = () => setIsCompactView(widthMq.matches);
    const onMotion = () => setPrefersReducedMotion(motionMq.matches);
    onWidth();
    onMotion();
    widthMq.addEventListener("change", onWidth);
    motionMq.addEventListener("change", onMotion);
    return () => {
      widthMq.removeEventListener("change", onWidth);
      motionMq.removeEventListener("change", onMotion);
    };
  }, []);

  const goToSlide = useCallback((idx) => {
    setSlideDirection(idx > activePreview ? 1 : idx < activePreview ? -1 : 1);
    setActivePreview(idx);
    setProgressKey((k) => k + 1);
  }, [activePreview]);

  // Auto-slide for carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setSlideDirection(1);
      setActivePreview((prev) => (prev + 1) % screenPreviews.length);
      setProgressKey((k) => k + 1);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const goPrev = () => {
    setSlideDirection(-1);
    setActivePreview((prev) =>
      prev === 0 ? screenPreviews.length - 1 : prev - 1,
    );
    setProgressKey((k) => k + 1);
  };

  const goNext = () => {
    setSlideDirection(1);
    setActivePreview((prev) => (prev + 1) % screenPreviews.length);
    setProgressKey((k) => k + 1);
  };

  const handleHeroMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    heroMouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    heroMouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleHeroMouseLeave = () => {
    heroMouseX.set(0);
    heroMouseY.set(0);
  };

  // Scroll to Screen Previews section

  const onEnquiryChange = (key) => (e) => {
    setEnquirySuccess("");
    setEnquiry((p) => ({ ...p, [key]: e.target.value }));
  };
  const resetEnquiry = () =>
    setEnquiry({
      storeName: "",
      mobileNumber: "",
      city: "",
      state: "",
      numberOfBranches: "1",
      description: "",
    });

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const headerOffset = 90; // sticky header approx
    const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  const handleScrollToScreens = () => scrollToId("screens");
  const handleScrollToDemo = () => scrollToId("demo");

  const validateMobile = (value) =>
    /^[6-9]\d{9}$/.test(String(value || "").trim());

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();

    const payload = {
      storeName: enquiry.storeName.trim(),
      mobileNumber: enquiry.mobileNumber.trim(),
      city: enquiry.city.trim(),
      state: enquiry.state.trim(),
      numberOfBranches: enquiry.numberOfBranches
        ? Number(enquiry.numberOfBranches)
        : 1,
      description: enquiry.description.trim(),
    };

    if (
      !payload.storeName ||
      !payload.mobileNumber ||
      !payload.city ||
      !payload.state
    ) {
      toast.error("Store name, Mobile, City, State required");
      return;
    }

    if (!validateMobile(payload.mobileNumber)) {
      toast.error("Enter valid 10-digit mobile number");
      return;
    }

    if (
      !payload.numberOfBranches ||
      Number.isNaN(payload.numberOfBranches) ||
      payload.numberOfBranches < 1
    ) {
      toast.error("Branches must be 1 or more");
      return;
    }

    try {
      await submitEnquiry(payload).unwrap();
      toast.success("Enquiry submitted! We will contact you soon.");
      setEnquirySuccess(
        "✅ Thanks! Your enquiry is submitted. Our team will contact you soon.",
      );
      setEnquirySuccess("");
      resetEnquiry();
    } catch (err) {
      toast.error(err?.data?.msg || "Failed to submit enquiry");
    }
  };

  const navItems = [
    { href: "#features", label: "Features" },
    { href: "#modules", label: "Modules" },
    { href: "#workflows", label: "Workflows" },
    { href: "#screens", label: "Screens" },
    { href: "/pricing", label: "Pricing" },
  ];

  const displayPrice =
    billing === "monthly"
      ? { amount: pricing.monthly, suffix: "/ month" }
      : { amount: pricing.yearly, suffix: "/ year" };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-[#f0f4ff] via-[#fdf9f3] to-[#e8f4fc] text-slate-900 antialiased">
      {/* soft pastel BG blobs + center glow */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(129,140,248,0.2),transparent),radial-gradient(ellipse_60%_40%_at_100%_100%,rgba(245,158,11,0.12),transparent),radial-gradient(ellipse_40%_30%_at_0%_100%,rgba(236,72,153,0.08),transparent)]" />
        <motion.div
          animate={floatOrb(12, 0)}
          className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#6366f1]/20 blur-3xl"
        />
        <motion.div
          animate={floatOrb(14, 1)}
          className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-[#f97316]/15 blur-3xl"
        />
        <motion.div
          animate={floatOrb(10, 0.5)}
          className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-400/[0.06] blur-3xl"
        />
        <motion.div
          animate={{ opacity: [0.04, 0.08, 0.04] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-soft-light"
        />
      </div>

      {/* NAVBAR */}
      <header
        className={`sticky top-0 z-40 border-b transition-all duration-300 ${
          scrolled
            ? "border-slate-200/80 bg-white/95 shadow-sm shadow-slate-200/50 backdrop-blur-xl"
            : "border-violet-100/70 bg-white/80 backdrop-blur-xl"
        }`}
      >
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 via-fuchsia-500/20 to-amber-500/15 ring-1 ring-violet-300/60 shadow-sm">
              <img
                src="/icon-512.png"
                alt="SwitchIn"
                className="h-5 w-5 object-contain"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-semibold tracking-tight text-slate-900">
                Switch<span className="text-violet-600">In</span>
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Jewellery Retail Software
              </span>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden items-center gap-7 text-sm text-slate-600 md:flex">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="group relative inline-flex items-center gap-1 font-medium text-slate-600 transition-colors hover:text-violet-700"
              >
                <span>{item.label}</span>
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-200 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* CTA + mobile menu */}
          <div className="flex items-center gap-2">
            <Link
              to={"/login"}
              className="hidden rounded-full border border-slate-200/90 bg-white px-4 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:border-violet-400 hover:text-violet-700 md:inline-flex"
            >
              Login
            </Link>
            <button
              onClick={handleScrollToDemo}
              type="button"
              className="hidden items-center gap-1.5 rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-slate-500/40 ring-1 ring-slate-800 hover:bg-black md:inline-flex"
            >
              Book free demo
              <ArrowRight className="h-3.5 w-3.5" />
            </button>

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMobileNavOpen((p) => !p)}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white p-1.5 text-slate-700 shadow-sm hover:border-violet-400 hover:text-violet-700 md:hidden"
            >
              {mobileNavOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile nav dropdown */}
        {mobileNavOpen && (
          <div className="border-t border-slate-200 bg-white/95 shadow-sm md:hidden">
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3 text-sm">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center justify-between rounded-xl px-2 py-2 text-slate-700 hover:bg-violet-50 hover:text-violet-700"
                >
                  <span>{item.label}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              ))}
              <div className="mt-2 flex flex-col gap-2">
                <Link
                  to={"/login"}
                  onClick={() => setMobileNavOpen(false)}
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-800 hover:border-violet-400 hover:text-violet-700"
                >
                  Login
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMobileNavOpen(false);
                    handleScrollToDemo();
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-slate-500/40 hover:bg-black"
                >
                  Book free demo
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-20 pt-10 md:px-6 md:pt-14">
        {/* HERO */}
        <section className="relative flex flex-col gap-10 md:flex-row md:items-center md:gap-12">
          {/* glow behind dashboard */}
          <div className="pointer-events-none absolute inset-y-6 right-0 -z-10 hidden w-1/2 md:block">
            <div className="h-full w-full rounded-[32px] bg-[radial-gradient(circle_at_center,_rgba(55,65,81,0.6),transparent_60%)] blur-2xl" />
          </div>

          {/* Left */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp(0)}
            className="flex-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900/90 px-3.5 py-1.5 text-[11px] font-medium text-slate-100 shadow-md shadow-slate-900/50 ring-1 ring-slate-700/80"
            >
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              <span>Made only for jewellery retail billing & stock</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 text-balance text-3xl font-semibold leading-[1.15] tracking-tight text-slate-900 sm:text-4xl md:text-5xl md:leading-[1.12]"
            >
              Manage your jewellery store on{" "}
              <motion.span
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                className="bg-[length:200%_auto] bg-gradient-to-r from-violet-700 via-fuchsia-500 to-amber-500 bg-clip-text text-transparent"
              >
                one clean system.
              </motion.span>
            </motion.h1>

            <p className="mt-4 max-w-xl text-sm leading-[1.65] text-slate-600 md:text-[15px]">
              SwitchIn connects your billing, stock, old gold/exchange, gold
              schemes, customers and reports in one place — built for purity,
              making, wastage and real jewellery workflows.
            </p>

            {/* Hero stat chips */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="mt-5 grid gap-3 text-[12px] text-slate-600 sm:grid-cols-3"
            >
              {[
                { icon: ReceiptIndianRupee, color: "text-emerald-600", label: "GST invoice ready formats" },
                { icon: Zap, color: "text-sky-600", label: "Fast billing + accurate totals" },
                { icon: Users, color: "text-violet-600", label: "Owners + staff role control" },
              ].map((chip, i) => (
                <motion.span
                  key={chip.label}
                  variants={staggerItem}
                  whileHover={{ y: -3, scale: 1.03 }}
                  className="inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 shadow-sm ring-1 ring-slate-200 backdrop-blur-sm"
                >
                  <chip.icon className={`h-3 w-3 ${chip.color}`} />
                  <span>{chip.label}</span>
                </motion.span>
              ))}
            </motion.div>

            {/* CTA buttons */}
            <div className="mt-7 flex w-full flex-col gap-3 text-xs sm:flex-row sm:flex-wrap sm:items-center">
              <motion.button
                type="button"
                onClick={handleScrollToDemo}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-700 via-fuchsia-600 to-amber-500 px-5 py-3 text-[13px] font-semibold text-white shadow-md shadow-violet-400/60 hover:brightness-110 hover:shadow-lg hover:shadow-violet-500/60 sm:w-auto sm:py-2.5"
              >
                Book free demo
                <ArrowRight className="h-4 w-4" />
              </motion.button>

              <Link
                to={"/login"}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-white/90 px-5 py-3 text-[13px] font-medium text-slate-800 shadow-sm hover:border-violet-400 hover:text-violet-700 sm:w-auto sm:py-2.5 md:hidden"
              >
                Login <ArrowRight className="h-4 w-4" />
              </Link>

              <button
                type="button"
                onClick={handleScrollToScreens}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-white/90 px-5 py-3 text-[13px] font-medium text-slate-800 shadow-sm hover:border-violet-400 hover:text-violet-700 sm:w-auto sm:py-2.5"
              >
                View sample screens
              </button>
            </div>

            {/* trust row */}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                <span>Jewellery-first billing & stock</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                <span>Training & support included</span>
              </div>
            </div>

            {/* trusted by strip */}
            <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Built for real jewellery shops
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px] font-medium text-slate-600">
                <span className="rounded-full bg-slate-100/80 px-3 py-1.5 ring-1 ring-slate-200/60">
                  Single store
                </span>
                <span className="rounded-full bg-slate-100/80 px-3 py-1.5 ring-1 ring-slate-200/60">
                  Multi-branch
                </span>
                <span className="rounded-full bg-slate-100/80 px-3 py-1.5 ring-1 ring-slate-200/60">
                  Gold & diamond retailers
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right - 3D dashboard preview */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0, rotateY: isCompactView ? 0 : 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            style={{ perspective: isCompactView ? undefined : 1200 }}
            className="flex-1 w-full"
          >
            <motion.div
              onMouseMove={isCompactView ? undefined : handleHeroMouseMove}
              onMouseLeave={isCompactView ? undefined : handleHeroMouseLeave}
              style={
                isCompactView
                  ? undefined
                  : {
                      rotateX: heroRotateX,
                      rotateY: heroRotateY,
                      transformStyle: "preserve-3d",
                    }
              }
              className="mx-auto w-full max-w-xl px-1 sm:px-0"
            >
              <motion.div
                animate={
                  prefersReducedMotion || isCompactView
                    ? undefined
                    : { y: [0, -8, 0] }
                }
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="rounded-[22px] bg-gradient-to-br from-violet-500/70 via-fuchsia-500/70 to-amber-400/70 p-[2px] shadow-2xl shadow-violet-400/50 sm:rounded-[26px]"
              >
                <div className="rounded-[24px] bg-white/95 p-4 shadow-xl shadow-violet-200/80 ring-1 ring-violet-100/90 backdrop-blur-sm">
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50/90 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
                        <Gem className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-900">
                          SwitchIn dashboard
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Today • Retail overview
                        </p>
                      </div>
                    </div>
                    <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
                      Jewellery Retail
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="rounded-2xl bg-slate-50 p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                      <p className="text-[10px] text-slate-500">
                        Today&apos;s sales
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        ₹ 4.2 L
                      </p>
                      <p className="mt-1 text-[10px] text-emerald-600">
                        +18% vs yesterday
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                      <p className="text-[10px] text-slate-500">Gold stock</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        12.47 kg
                      </p>
                      <p className="mt-1 text-[10px] text-slate-500">
                        22K • 18K • 14K
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                      <p className="text-[10px] text-slate-500">
                        Active schemes
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        327
                      </p>
                      <p className="mt-1 text-[10px] text-emerald-600">
                        On track
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    <div className="rounded-2xl bg-slate-50 p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                      <div className="flex items-center justify-between text-[11px] text-slate-700">
                        <span>Billing & invoices</span>
                        <ReceiptIndianRupee className="h-3.5 w-3.5 text-emerald-600" />
                      </div>
                      <ul className="mt-2 space-y-1.5 text-[11px] text-slate-500">
                        <li className="flex items-center justify-between">
                          <span>A4/A5 invoice templates</span>
                          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[9px] text-violet-700">
                            Jewellery layout
                          </span>
                        </li>
                        <li>Auto making & wastage calculation</li>
                        <li>Old gold/exchange in same bill</li>
                      </ul>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                      <div className="flex items-center justify-between text-[11px] text-slate-700">
                        <span>Reminders</span>
                        <Bell className="h-3.5 w-3.5 text-sky-500" />
                      </div>
                      <ul className="mt-2 space-y-1.5 text-[11px] text-slate-500">
                        <li className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Pending customer follow-ups
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                          Scheme instalments due this week
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                          Daily closing pending
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* mobile app pill */}
                  <button
                    type="button"
                    onClick={() => {
                      if (canInstall) install();
                      else
                        alert(
                          "Open this site in Chrome on mobile to install the app",
                        );
                    }}
                    className="mt-3 flex w-full items-center justify-between rounded-2xl bg-slate-900 px-3 py-2 text-slate-50 shadow-[0_10px_30px_rgba(15,23,42,0.55)] hover:bg-black transition"
                  >
                    <div className="flex items-center gap-2">
                      <div className="rounded-xl bg-slate-800 p-1.5">
                        <Smartphone className="h-3.5 w-3.5" />
                      </div>
                      <div className="text-[10px] text-left">
                        <p className="font-medium">Owner mobile view</p>
                        <p className="text-slate-400">
                          Key numbers on your phone
                        </p>
                      </div>
                    </div>

                    {isInstalled && (
                      <span className="text-[9px] text-emerald-400">
                        Installed ✓
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </section>

        {/* FEATURES */}
        <section id="features" className="mt-16 space-y-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="max-w-xl"
          >
            <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              Built for jewellery retail
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              A complete operating system for jewellery stores.
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Every screen understands purity, making, wastage, stones, old gold
              and schemes — because it&apos;s made for jewellery, not generic
              shops.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {[
              {
                icon: <Layers className="h-4 w-4" />,
                title: "Complete store control",
                desc: "From stock inward to billing and returns, everything stays in one system.",
              },
              {
                icon: <Zap className="h-4 w-4" />,
                title: "Fast, accurate billing",
                desc: "Auto making, wastage, discount and GST calculation with jewellery invoice formats.",
              },
              {
                icon: <Users className="h-4 w-4" />,
                title: "Customer & CRM",
                desc: "Purchase history, scheme activity, follow-ups and notes for every customer.",
              },
              {
                icon: <BarChart3 className="h-4 w-4" />,
                title: "Clear insights",
                desc: "Track best-selling designs, categories, margins and store performance.",
              },
              {
                icon: <ShieldCheck className="h-4 w-4" />,
                title: "Roles & approvals",
                desc: "Owner control on editing, discount, delete, approvals and audit history.",
              },
              {
                icon: <Smartphone className="h-4 w-4" />,
                title: "Owner view on mobile",
                desc: "See sales, outstanding and collections on your phone anytime.",
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={staggerItem}
                whileHover={{
                  rotateX: -4,
                  rotateY: 4,
                  y: -8,
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 260, damping: 18 },
                }}
                style={{ transformStyle: "preserve-3d", transformPerspective: 1000 }}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-sm transition-colors duration-300 hover:border-violet-200/80 hover:shadow-xl hover:shadow-violet-100/60 hover:ring-1 hover:ring-violet-100"
              >
                <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-violet-500/8 to-fuchsia-500/8 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-700 transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-violet-500 group-hover:to-fuchsia-500 group-hover:text-white group-hover:shadow-md group-hover:shadow-violet-200/50">
                  {item.icon}
                </div>
                <h3 className="mt-3.5 text-sm font-semibold text-slate-900">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* MODULES */}
        <section id="modules" className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
          >
            <div>
              <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
                Core modules
              </p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                Everything you need for daily jewellery retail.
              </h2>
            </div>
            <p className="max-w-md text-[13px] text-slate-600">
              Start with billing & stock, then add schemes and advanced reports
              as you grow.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            {[
              {
                label: "Stock & Item Management",
                icon: <ShoppingBag className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "Retail Billing & GST",
                icon: <ReceiptIndianRupee className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "Old Gold / Exchange",
                icon: <History className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "Customer & CRM",
                icon: <Users className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "Daily Transactions",
                icon: <Layers className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "Gold Scheme / Saving Plan",
                icon: <Sparkles className="h-4 w-4" />,
                tag: "Optional",
              },
              {
                label: "Reports & Analytics",
                icon: <FileSpreadsheet className="h-4 w-4" />,
                tag: "Included",
              },
              {
                label: "User, Role & Branch Control",
                icon: <ShieldCheck className="h-4 w-4" />,
                tag: "Included",
              },
            ].map((m, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white/95 px-4 py-3.5 text-[13px] text-slate-900 shadow-sm transition-all duration-300 hover:border-violet-200/80 hover:shadow-md hover:ring-1 hover:ring-violet-100"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors duration-300 hover:bg-violet-100 hover:text-violet-700">
                    {m.icon}
                  </div>
                  <span className="font-medium text-slate-800">{m.label}</span>
                </div>
                <span className="shrink-0 rounded-full bg-violet-50 px-2.5 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200/60">
                  {m.tag}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* WORKFLOWS */}
        <section id="workflows" className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="max-w-2xl"
          >
            <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              Daily retail flows
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Built around how jewellery stores work every day.
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              From morning checks to closing, SwitchIn keeps billing, stock,
              schemes and reports in sync.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mt-6 grid gap-4 md:grid-cols-3"
          >
            <motion.div
              variants={staggerItem}
              className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-violet-200/80 hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  <ShoppingBag className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Counter sales & invoices
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Fast billing + accurate totals
                  </p>
                </div>
              </div>
              <ul className="mt-3.5 space-y-1.5 text-slate-600">
                <li>• Search by item code, design, purity</li>
                <li>• Auto making, wastage & discount</li>
                <li>• Old gold/exchange in one invoice</li>
                <li>• Split payments with multiple modes</li>
              </ul>
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-emerald-200/80 hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                  <History className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Old gold & exchange
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Clear record for margin & audit
                  </p>
                </div>
              </div>
              <ul className="mt-3.5 space-y-1.5 text-slate-600">
                <li>• Capture purity, deductions & net weight</li>
                <li>• Adjust against invoice or cash payout</li>
                <li>• Track metal movement cleanly</li>
                <li>• Proper records for CA & audit</li>
              </ul>
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-sky-200/80 hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <Clock3 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Daily closing & reports
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Simple end-of-day routine
                  </p>
                </div>
              </div>
              <ul className="mt-3.5 space-y-1.5 text-slate-600">
                <li>• Sales summary and outstanding</li>
                <li>• Scheme collection summary</li>
                <li>• Cash/bank reconciliation</li>
                <li>• Export to Excel / PDF</li>
              </ul>
            </motion.div>
          </motion.div>
        </section>

        {/* SCREEN PREVIEWS CAROUSEL */}
        <section id="screens" className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
          >
            <div>
              <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
                Screen previews
              </p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                Modern screens designed for jewellery retail work.
              </h2>
            </div>
            <p className="max-w-md text-[13px] text-slate-600">
              No clutter. Only the fields your staff actually needs — purity,
              making, wastage, old gold, schemes and payments.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp(0.05)}
            className="mt-5"
          >
            <div className="relative overflow-hidden rounded-3xl border border-violet-200/60 bg-white/95 p-4 shadow-xl shadow-violet-200/40 backdrop-blur sm:p-6">
              {/* soft glow */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-violet-400/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-16 -left-12 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />

              {/* ✅ FIXED HEIGHT WRAPPER (same on all screens) */}
              <div className="relative grid gap-4 md:grid-cols-[1.15fr,0.85fr] md:items-stretch">
                {/* LEFT: Mock UI Preview (fixed height) */}
                <div className="h-auto min-h-[420px] sm:min-h-[520px] md:min-h-[560px]">
                  <motion.div
                    key={`preview-${activePreview}`}
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="h-full rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-[0_22px_60px_rgba(15,23,42,0.45)]"
                  >
                    {/* Top app bar */}
                    <div className="flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 text-white shadow-md">
                          <Gem className="h-4 w-4" />
                        </div>
                        <div className="leading-tight">
                          <p className="text-xs font-semibold text-white">
                            SwitchIn Preview
                          </p>
                          <p className="text-[11px] text-white/60">
                            {screenPreviews[activePreview]?.title}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-white/80 ring-1 ring-white/10">
                          Retail
                        </span>
                        <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-200 ring-1 ring-emerald-300/20">
                          Live
                        </span>
                      </div>
                    </div>

                    {/* ✅ SLIDE CONTENT (same as your current: Billing/Stock/Scheme/Customer) */}
                    {activePreview === 0 && (
                      <>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[10px] text-white/60">
                              Bill Total
                            </p>
                            <p className="mt-1 text-sm font-semibold text-white">
                              ₹ 1,04,280
                            </p>
                            <p className="mt-1 text-[10px] text-emerald-200">
                              GST included
                            </p>
                          </div>
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[10px] text-white/60">
                              Old Gold
                            </p>
                            <p className="mt-1 text-sm font-semibold text-white">
                              ₹ 18,500
                            </p>
                            <p className="mt-1 text-[10px] text-amber-200">
                              Adjusted
                            </p>
                          </div>
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[10px] text-white/60">Balance</p>
                            <p className="mt-1 text-sm font-semibold text-white">
                              ₹ 2,200
                            </p>
                            <p className="mt-1 text-[10px] text-white/60">
                              Split payment
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 grid gap-2 md:grid-cols-[1fr,0.9fr]">
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <div className="flex items-center justify-between">
                              <p className="text-[11px] font-semibold text-white/90">
                                Items
                              </p>
                              <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[9px] font-semibold text-violet-200 ring-1 ring-violet-300/20">
                                Purity • Making • Wastage
                              </span>
                            </div>

                            <div className="mt-3 space-y-2">
                              {[0, 1, 2, 3].map((r) => (
                                <div
                                  key={r}
                                  className="flex items-center gap-2"
                                >
                                  <div className="h-3 flex-1 rounded bg-white/10" />
                                  <div className="h-3 w-14 rounded bg-white/10" />
                                  <div className="h-3 w-16 rounded bg-white/10" />
                                </div>
                              ))}
                            </div>

                            <div className="mt-3 rounded-xl bg-white/10 p-3 ring-1 ring-white/10">
                              <div className="flex items-center justify-between text-[11px] text-white/80">
                                <span>Grand Total</span>
                                <span className="font-semibold text-white">
                                  ₹ 1,04,280
                                </span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-[10px] font-semibold text-emerald-200 ring-1 ring-emerald-300/20">
                                  Cash
                                </span>
                                <span className="rounded-full bg-sky-400/15 px-2.5 py-1 text-[10px] font-semibold text-sky-200 ring-1 ring-sky-300/20">
                                  UPI
                                </span>
                                <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-semibold text-amber-200 ring-1 ring-amber-300/20">
                                  Card
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[11px] font-semibold text-white/90">
                              Quick actions
                            </p>
                            <div className="mt-3 grid gap-2">
                              <div className="h-10 rounded-2xl bg-white/10 ring-1 ring-white/10" />
                              <div className="h-10 rounded-2xl bg-white/10 ring-1 ring-white/10" />
                              <div className="h-10 rounded-2xl bg-emerald-400/15 ring-1 ring-emerald-300/20" />
                            </div>
                            <p className="mt-3 text-[10px] text-white/60">
                              Save • Print • WhatsApp
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {activePreview === 1 && (
                      <>
                        {/* Keep your STOCK UI here (same as your current) */}
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {[
                            {
                              t: "Total Stock",
                              v: "₹ 2.1 Cr",
                              sub: "All categories",
                            },
                            { t: "Fast Moving", v: "148", sub: "Designs" },
                            { t: "Low Stock", v: "17", sub: "Alerts" },
                          ].map((x) => (
                            <div
                              key={x.t}
                              className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10"
                            >
                              <p className="text-[10px] text-white/60">{x.t}</p>
                              <p className="mt-1 text-sm font-semibold text-white">
                                {x.v}
                              </p>
                              <p className="mt-1 text-[10px] text-white/60">
                                {x.sub}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 grid gap-2 md:grid-cols-[0.9fr,1.1fr]">
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <div className="flex items-center justify-between">
                              <p className="text-[11px] font-semibold text-white/90">
                                Filters
                              </p>
                              <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[9px] font-semibold text-violet-200 ring-1 ring-violet-300/20">
                                Branch-wise
                              </span>
                            </div>
                            <div className="mt-3 space-y-2">
                              {[1, 2, 3, 4].map((i) => (
                                <div
                                  key={i}
                                  className="h-9 rounded-2xl bg-white/10 ring-1 ring-white/10"
                                />
                              ))}
                            </div>
                            <div className="mt-3 h-10 rounded-2xl bg-amber-400/15 ring-1 ring-amber-300/20" />
                          </div>

                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <div className="flex items-center justify-between">
                              <p className="text-[11px] font-semibold text-white/90">
                                Stock Snapshot
                              </p>
                              <span className="text-[10px] text-white/60">
                                Purity-wise
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {[
                                { k: "22K", w: "7.20 kg" },
                                { k: "18K", w: "3.10 kg" },
                                { k: "14K", w: "1.00 kg" },
                                { k: "Silver", w: "1.17 kg" },
                              ].map((x) => (
                                <div
                                  key={x.k}
                                  className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10"
                                >
                                  <p className="text-[10px] text-white/60">
                                    {x.k}
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-white">
                                    {x.w}
                                  </p>
                                </div>
                              ))}
                            </div>

                            <div className="mt-3 rounded-2xl bg-rose-400/10 p-3 ring-1 ring-rose-300/20">
                              <p className="text-[10px] font-semibold text-rose-200">
                                Low stock alerts
                              </p>
                              <div className="mt-2 space-y-2">
                                {[1, 2, 3].map((i) => (
                                  <div
                                    key={i}
                                    className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2 ring-1 ring-white/10"
                                  >
                                    <span className="text-[11px] text-white/80">
                                      Chain • 22K
                                    </span>
                                    <span className="text-[11px] font-semibold text-white">
                                      2 pcs
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {activePreview === 2 && (
                      <>
                        {/* Keep your SCHEME UI here (same as your current) */}
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {[
                            { t: "Active Members", v: "327", sub: "Running" },
                            {
                              t: "Collections",
                              v: "₹ 3.8 L",
                              sub: "This month",
                            },
                            { t: "Maturing", v: "21", sub: "Next 30 days" },
                          ].map((x) => (
                            <div
                              key={x.t}
                              className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10"
                            >
                              <p className="text-[10px] text-white/60">{x.t}</p>
                              <p className="mt-1 text-sm font-semibold text-white">
                                {x.v}
                              </p>
                              <p className="mt-1 text-[10px] text-white/60">
                                {x.sub}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 grid gap-2 md:grid-cols-2">
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[11px] font-semibold text-white/90">
                              Collection progress
                            </p>
                            <div className="mt-3 space-y-3">
                              {[
                                { name: "Monthly target", p: "72%" },
                                { name: "Due reminders", p: "58%" },
                                { name: "Maturity tracking", p: "88%" },
                              ].map((x) => (
                                <div key={x.name}>
                                  <div className="flex items-center justify-between text-[11px] text-white/70">
                                    <span>{x.name}</span>
                                    <span className="font-semibold text-white">
                                      {x.p}
                                    </span>
                                  </div>
                                  <div className="mt-2 h-2 w-full rounded-full bg-white/10">
                                    <div className="h-2 w-2/3 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <div className="flex items-center justify-between">
                              <p className="text-[11px] font-semibold text-white/90">
                                Members
                              </p>
                              <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[9px] font-semibold text-amber-200 ring-1 ring-amber-300/20">
                                Due list
                              </span>
                            </div>
                            <div className="mt-3 space-y-2">
                              {[1, 2, 3, 4].map((i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between rounded-2xl bg-white/10 px-3 py-2 ring-1 ring-white/10"
                                >
                                  <div>
                                    <p className="text-[11px] font-semibold text-white">
                                      Member #{120 + i}
                                    </p>
                                    <p className="text-[10px] text-white/60">
                                      Installment due • 5th
                                    </p>
                                  </div>
                                  <span className="text-[11px] font-semibold text-white">
                                    ₹ 2,000
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {activePreview === 3 && (
                      <>
                        {/* Keep your CUSTOMER UI here (same as your current) */}
                        <div className="mt-3 rounded-3xl bg-white/5 p-4 ring-1 ring-white/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-violet-500/70 to-fuchsia-500/70 ring-1 ring-white/10" />
                              <div>
                                <p className="text-sm font-semibold text-white">
                                  Aarti Mehta
                                </p>
                                <p className="text-[11px] text-white/60">
                                  VIP • Mumbai
                                </p>
                              </div>
                            </div>
                            <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-[10px] font-semibold text-emerald-200 ring-1 ring-emerald-300/20">
                              Follow-up today
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 grid gap-2 md:grid-cols-2">
                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[11px] font-semibold text-white/90">
                              Recent timeline
                            </p>
                            <div className="mt-3 space-y-2">
                              {[
                                "Purchased • Diamond ring",
                                "Scheme installment received",
                                "Birthday reminder set",
                                "Called for new collection",
                              ].map((t, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-2 rounded-2xl bg-white/10 px-3 py-2 ring-1 ring-white/10"
                                >
                                  <span className="h-2 w-2 rounded-full bg-amber-300" />
                                  <span className="text-[11px] text-white/80">
                                    {t}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                            <p className="text-[11px] font-semibold text-white/90">
                              Notes & preferences
                            </p>
                            <div className="mt-3 space-y-2">
                              <div className="h-10 rounded-2xl bg-white/10 ring-1 ring-white/10" />
                              <div className="h-10 rounded-2xl bg-white/10 ring-1 ring-white/10" />
                              <div className="h-10 rounded-2xl bg-violet-500/15 ring-1 ring-violet-300/20" />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* bottom hint row */}
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/70">
                        <span className="rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-white/10">
                          Keyboard friendly
                        </span>
                        <span className="rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-white/10">
                          Fast workflow
                        </span>
                        <span className="rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-white/10">
                          Less clicks
                        </span>
                      </div>
                      <span className="text-[11px] text-white/60">
                        Designed for counter staff
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* RIGHT: Details panel (fixed height + scroll) */}
                <div className="h-auto min-h-[420px] sm:min-h-[520px] md:min-h-[560px]">
                  <motion.div
                    key={`details-${activePreview}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="h-full overflow-hidden rounded-3xl border border-slate-200/80 bg-white/90 p-5 shadow-sm"
                  >
                    <div className="h-full overflow-auto pr-1">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-600">
                        Screen {String(activePreview + 1).padStart(2, "0")} of{" "}
                        {String(screenPreviews.length).padStart(2, "0")}
                      </p>

                      <h3 className="mt-2 text-base font-semibold text-slate-900">
                        {screenPreviews[activePreview]?.title}
                      </h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
                        {screenPreviews[activePreview]?.desc}
                      </p>

                      <div className="mt-4 grid gap-2">
                        {(activePreview === 0
                          ? [
                              {
                                label: "Purity + making + wastage fields",
                                tone: "violet",
                              },
                              {
                                label: "Old gold / exchange built-in",
                                tone: "amber",
                              },
                              {
                                label: "Split payments & quick totals",
                                tone: "emerald",
                              },
                              {
                                label: "Print / WhatsApp invoice",
                                tone: "sky",
                              },
                            ]
                          : activePreview === 1
                            ? [
                                {
                                  label: "Purity-wise stock snapshot",
                                  tone: "violet",
                                },
                                { label: "Branch-wise filters", tone: "sky" },
                                { label: "Low stock alerts", tone: "amber" },
                                {
                                  label: "Fast moving designs",
                                  tone: "emerald",
                                },
                              ]
                            : activePreview === 2
                              ? [
                                  {
                                    label: "Installment schedule view",
                                    tone: "violet",
                                  },
                                  {
                                    label: "Due reminder tracking",
                                    tone: "amber",
                                  },
                                  {
                                    label: "Maturity monitoring",
                                    tone: "emerald",
                                  },
                                  {
                                    label: "Collection summary daily",
                                    tone: "sky",
                                  },
                                ]
                              : [
                                  {
                                    label: "Full purchase history",
                                    tone: "violet",
                                  },
                                  {
                                    label: "Follow-up reminders",
                                    tone: "emerald",
                                  },
                                  {
                                    label: "Occasions & preferences",
                                    tone: "amber",
                                  },
                                  { label: "Notes for staff", tone: "sky" },
                                ]
                        ).map((x, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between rounded-2xl border border-slate-200/70 bg-white px-3.5 py-2.5 text-[13px] text-slate-700 shadow-sm"
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  x.tone === "violet"
                                    ? "bg-violet-500"
                                    : x.tone === "amber"
                                      ? "bg-amber-400"
                                      : x.tone === "emerald"
                                        ? "bg-emerald-500"
                                        : "bg-sky-500"
                                }`}
                              />
                              <span className="font-medium">{x.label}</span>
                            </div>
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          </div>
                        ))}
                      </div>

                      <div className="mt-5 flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={handleScrollToScreens}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-800 shadow-sm hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                        >
                          View more screens
                          <ArrowRight className="h-4 w-4" />
                        </button>
                        <p className="text-[11px] text-slate-500">
                          We can customise invoice format, columns and report
                          layouts for your store.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* ✅ Tabs + Dots + Controls (dots always visible) */}
              <div className="relative mt-5 rounded-2xl border border-slate-200/80 bg-white/90 p-3 shadow-sm">
                {/* tabs */}
                <div className="flex flex-wrap items-center gap-2">
                  {screenPreviews.map((item, idx) => (
                    <button
                      key={item.title}
                      type="button"
                      onClick={() => setActivePreview(idx)}
                      className={`rounded-full px-3.5 py-2 text-[12px] font-semibold transition ${
                        idx === activePreview
                          ? "bg-slate-900 text-white shadow-sm"
                          : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-violet-50 hover:text-violet-700"
                      }`}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>

                {/* dots */}
                <div className="mt-3 flex items-center justify-center gap-2">
                  {screenPreviews.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActivePreview(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        idx === activePreview
                          ? "w-6 bg-gradient-to-r from-violet-600 to-fuchsia-500"
                          : "w-2 bg-slate-300 hover:bg-slate-400"
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>

                {/* progress */}
                {/* <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-400 transition-all duration-500"
            style={{ width: `${((activePreview + 1) / screenPreviews.length) * 100}%` }}
          />
        </div> */}

                {/* arrows */}
                {/* <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            onClick={goPrev}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[12px] font-semibold text-slate-700 shadow-sm hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>

          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[12px] font-semibold text-slate-700 shadow-sm hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div> */}
              </div>
            </div>
          </motion.div>
        </section>
        {/* DEMO */}
        <section
          className="mx-auto mt-16 max-w-2xl"
        >
          {false && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="relative overflow-hidden rounded-3xl border border-violet-200/70 bg-white/95 p-6 shadow-lg shadow-violet-200/40"
          >
            <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-gradient-to-br from-violet-400/10 to-fuchsia-400/10 blur-2xl" />
            <div className="relative">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
                Pricing
              </p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
                Simple pricing for jewellery retailers.
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Start small and scale later. Best value is the yearly plan —
                most stores choose yearly.
              </p>

              {/* Toggle */}
              <div className="mt-5 inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setBilling("monthly")}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                    billing === "monthly"
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBilling("yearly")}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                    billing === "yearly"
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Yearly
                </button>
              </div>

              {/* Price */}
              <div className="mt-5 flex flex-wrap items-baseline gap-2">
                <span className="bg-gradient-to-r from-violet-700 to-fuchsia-500 bg-clip-text text-3xl font-semibold text-transparent">
                  ₹
                  <span className="tabular-nums">
                    {displayPrice.amount.toLocaleString("en-IN")}
                  </span>
                </span>
                <span className="text-sm font-medium text-slate-600">
                  {displayPrice.suffix}
                </span>
              </div>

              {billing === "yearly" && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200/60">
                  Best value • Effective ₹
                  {pricing.effectiveMonthly.toLocaleString("en-IN")}/month •
                  Save ₹{pricing.savings.toLocaleString("en-IN")}/year
                </div>
              )}

              <ul className="mt-5 space-y-2.5 text-[13px] text-slate-700">
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  Unlimited invoices & customers
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  Secure cloud backup & access control
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  Setup, onboarding & staff training included
                </li>
              </ul>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/register"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-700 via-fuchsia-500 to-amber-500 px-5 py-3 text-[13px] font-semibold text-white shadow-lg shadow-violet-400/50 transition-all hover:shadow-xl hover:shadow-violet-400/60"
                >
                  Get started
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-[11px] leading-relaxed text-slate-600 sm:max-w-[220px]">
                  Yearly plan recommended for stores. Monthly is available for
                  trials.
                </p>
              </div>
            </div>
          </motion.div>
          )}

          {/* Demo form */}
          <motion.div
            id="demo"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0.05)}
            className="rounded-3xl border border-slate-200/80 bg-white/95 p-6 shadow-lg shadow-slate-200/30"
          >
            <h3 className="text-base font-semibold text-slate-900">
              Schedule a 20-minute discovery call
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
              Share a few details and our team will show how SwitchIn fits your
              jewellery workflow.
            </p>
            {enquirySuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
                {enquirySuccess}
              </div>
            )}

            <form
              onSubmit={handleSubmitEnquiry}
              className="mt-5 space-y-4 text-[13px]"
            >
              {/* Store Name */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  Store name
                </label>
                <input
                  value={enquiry.storeName}
                  onChange={onEnquiryChange("storeName")}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="e.g. Motiwala Jewellers"
                />
              </div>
              {/* Mobile */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  Mobile / WhatsApp
                </label>
                <input
                  value={enquiry.mobileNumber}
                  onChange={onEnquiryChange("mobileNumber")}
                  inputMode="numeric"
                  maxLength={10}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="10-digit mobile number"
                />
              </div>
              {/* City */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  City
                </label>
                <input
                  value={enquiry.city}
                  onChange={onEnquiryChange("city")}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="e.g. Mumbai"
                />
              </div>
              {/* State */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  State
                </label>
                <input
                  value={enquiry.state}
                  onChange={onEnquiryChange("state")}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="e.g. Maharashtra"
                />
              </div>
              {/* Branches */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  Number of branches
                </label>
                <input
                  value={enquiry.numberOfBranches}
                  onChange={onEnquiryChange("numberOfBranches")}
                  inputMode="numeric"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="e.g. 2"
                />
              </div>
              {/* Description */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  What are you looking for?
                </label>
                <textarea
                  rows={3}
                  value={enquiry.description}
                  onChange={onEnquiryChange("description")}
                  className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-2 focus:ring-violet-500/20"
                  placeholder="Billing & stock / Old gold / Gold schemes / Reports / Multi-branch etc."
                />
              </div>
              {/* Submit */}
              <button
                type="submit"
                disabled={enquiryLoading}
                className={`mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-[13px] font-semibold text-white shadow-md transition focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                  enquiryLoading
                    ? "bg-slate-700 cursor-not-allowed"
                    : "bg-slate-900 hover:bg-slate-800"
                }`}
              >
                {enquiryLoading ? "Submitting..." : "Submit enquiry"}
              </button>
              <AnimatePresence>
                {isSuccess && (
                  <motion.div
                    key="success-banner"
                    initial={{ opacity: 0, y: -15, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="mt-4 overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 px-4 py-3 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>

                      <div className="flex-1">
                        <p className="text-[13px] font-semibold text-emerald-800">
                          Enquiry submitted successfully
                        </p>
                        <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">
                          Thanks! Our team will contact you soon on WhatsApp /
                          call.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>{" "}
            </form>
          </motion.div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="max-w-2xl"
          >
            <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              Who is SwitchIn for?
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Made for jewellery retailers at different stages.
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Whether you&apos;re moving from manual billing or upgrading from a
              generic software, SwitchIn keeps your store clean and controlled.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mt-6 grid gap-4 md:grid-cols-3"
          >
            {[
              {
                title: "Single-store jewellers",
                icon: <ShoppingBag className="h-4 w-4" />,
                bg: "bg-violet-100 text-violet-700",
                text: "Move from manual bills and Excel to one clean billing + stock + reports system built for jewellery.",
              },
              {
                title: "Multi-branch retailers",
                icon: <Users className="h-4 w-4" />,
                bg: "bg-emerald-100 text-emerald-700",
                text: "Separate stock per branch, role-wise logins and consolidated reports for owners.",
              },
              {
                title: "Growing brands",
                icon: <BarChart3 className="h-4 w-4" />,
                bg: "bg-amber-100 text-amber-700",
                text: "Add schemes, CRM and advanced reports on the same platform as your business grows.",
              },
            ].map((c, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-violet-200/80 hover:shadow-md"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${c.bg}`}
                  >
                    {c.icon}
                  </div>
                  <p className="text-sm font-semibold text-slate-900">
                    {c.title}
                  </p>
                </div>
                <p className="leading-relaxed text-slate-600">{c.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* IMPLEMENTATION & SUPPORT */}
        <section className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="max-w-2xl"
          >
            <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              Implementation & support
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Move without chaos.
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              We migrate data, train staff and support you so billing and stock
              operations run smoothly.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mt-6 grid gap-4 md:grid-cols-4"
          >
            {[
              {
                icon: <ClipboardList className="h-4 w-4" />,
                title: "1. Discovery",
                desc: "We understand your invoice formats, stock structure and workflow.",
              },
              {
                icon: <Database className="h-4 w-4" />,
                title: "2. Setup & data",
                desc: "We configure masters, categories and import clean data.",
              },
              {
                icon: <Users className="h-4 w-4" />,
                title: "3. Training",
                desc: "Role-wise training for billing staff, managers and owners.",
              },
              {
                icon: <Headset className="h-4 w-4" />,
                title: "4. Go-live & help",
                desc: "Go-live support plus ongoing help via calls and remote sessions.",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                className="rounded-2xl border border-slate-200/80 bg-white/95 p-4 text-[13px] shadow-sm transition-all duration-300 hover:border-violet-200/80 hover:shadow-md"
              >
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  {step.icon}
                </div>
                <p className="text-sm font-semibold text-slate-900">
                  {step.title}
                </p>
                <p className="mt-1.5 leading-relaxed text-slate-600">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-emerald-200/80 hover:shadow-md">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <Cloud className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Cloud access with control
                </p>
                <p className="mt-1.5 leading-relaxed text-slate-600">
                  Access from store, office or home with proper roles and
                  logins.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-sky-200/80 hover:shadow-md">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                <Lock className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Audit & approvals
                </p>
                <p className="mt-1.5 leading-relaxed text-slate-600">
                  Key actions logged. Permissions for edit, discount, delete &
                  approvals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp(0)}
            className="max-w-2xl"
          >
            <div className="h-[2px] w-16 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              FAQ
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Questions jewellery retailers usually ask.
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="mt-6 grid gap-4 md:grid-cols-2"
          >
            {[
              {
                q: "Do you support multi-branch stores?",
                a: "Yes. Manage multiple branches with separate stock, users and consolidated reports from one login.",
              },
              {
                q: "Can you match our existing invoice design?",
                a: "Yes. We can customise invoice layout (logo, terms, signatures) to match your store format.",
              },
              {
                q: "How long does implementation take?",
                a: "For a single store, go-live is usually within 7–14 days depending on data readiness.",
              },
              {
                q: "Do you provide training & support?",
                a: "Yes. We train billing staff + owners, and provide support during go-live and after.",
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={staggerItem}
                className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 text-[13px] shadow-sm transition-all duration-300 hover:border-violet-200/80 hover:shadow-md"
              >
                <p className="text-sm font-semibold text-slate-900">{item.q}</p>
                <p className="mt-2 leading-relaxed text-slate-600">{item.a}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* FOOTER */}
        <footer className="mt-20 rounded-2xl border border-slate-200/80 bg-white/80 px-6 py-8 backdrop-blur-sm">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 ring-1 ring-violet-200/60">
                <img
                  src="/icon-512.png"
                  alt=""
                  className="h-4 w-4 object-contain"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-800">
                  Switch<span className="text-violet-600">In</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  Jewellery Retail Software
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 text-[12px]">
              <Link
                to={"/pricing"}
                className="font-medium text-slate-600 transition hover:text-violet-700"
              >
                Pricing
              </Link>
              <Link
                to={"/terms"}
                className="font-medium text-slate-600 transition hover:text-violet-700"
              >
                Terms
              </Link>
              <Link
                to={"/privacy"}
                className="font-medium text-slate-600 transition hover:text-violet-700"
              >
                Privacy
              </Link>
              
              <Link
                to={"/support"}
                className="font-medium text-slate-600 transition hover:text-violet-700"
              >
                Support
              </Link>
            </div>
          </div>
          <p className="mt-6 border-t border-slate-200/80 pt-6 text-[11px] text-slate-500">
            © {new Date().getFullYear()} SwitchIn. All rights reserved.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default SwitchInLandingPage;
